import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const categories = [
  { value: 'homepage', label: 'Anasayfa' },
  { value: 'navigation', label: 'Navigasyon' },
  { value: 'buttons', label: 'Butonlar' },
  { value: 'placeholders', label: 'Placeholder\'lar' },
  { value: 'messages', label: 'Mesajlar' },
  { value: 'admin', label: 'Admin Paneli' },
  { value: 'other', label: 'Diğer' }
];

const TextManagement = () => {
  const { toast } = useToast();
  const [texts, setTexts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingText, setEditingText] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [formData, setFormData] = useState({
    key: '',
    value: '',
    description: '',
    category: 'homepage'
  });

  useEffect(() => {
    loadTexts();
  }, []);

  const loadTexts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${BACKEND_URL}/api/admin/site-texts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTexts(data);
    } catch (error) {
      console.error('Error loading texts:', error);
    }
  };

  const initDefaults = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${BACKEND_URL}/api/admin/site-texts/init-defaults`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({
          title: '✅ Başarılı!',
          description: 'Varsayılan yazılar eklendi.',
        });
        loadTexts();
      }
    } catch (error) {
      toast({
        title: '❌ Hata!',
        description: 'Varsayılan yazılar eklenirken hata oluştu.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (text = null) => {
    if (text) {
      setEditingText(text);
      setFormData({
        key: text.key,
        value: text.value,
        description: text.description,
        category: text.category
      });
    } else {
      setEditingText(null);
      setFormData({
        key: '',
        value: '',
        description: '',
        category: 'homepage'
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const url = editingText 
        ? `${BACKEND_URL}/api/admin/site-texts/${editingText.id}`
        : `${BACKEND_URL}/api/admin/site-texts`;
      
      const response = await fetch(url, {
        method: editingText ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({
          title: '✅ Başarılı!',
          description: editingText ? 'Yazı güncellendi!' : 'Yazı eklendi!',
        });
        setIsDialogOpen(false);
        loadTexts();
      }
    } catch (error) {
      toast({
        title: '❌ Hata!',
        description: 'İşlem başarısız oldu.',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu yazıyı silmek istediğinize emin misiniz?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${BACKEND_URL}/api/admin/site-texts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast({
        title: '✅ Başarılı!',
        description: 'Yazı silindi.',
      });
      loadTexts();
    } catch (error) {
      toast({
        title: '❌ Hata!',
        description: 'Silme işlemi başarısız oldu.',
        variant: 'destructive'
      });
    }
  };

  const filteredTexts = selectedCategory === 'all' 
    ? texts 
    : texts.filter(t => t.category === selectedCategory);

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#5C4033]">Yazı Yönetimi</h1>
          <p className="text-[#8B7355] mt-1">Frontend'deki tüm yazıları buradan yönetin</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={initDefaults}
            variant="outline"
            disabled={loading}
            className="border-[#D5C4A1]"
          >
            <FileText className="h-4 w-4 mr-2" />
            Varsayılanları Yükle
          </Button>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-[#8B7355] hover:bg-[#6B5744]"
          >
            <Plus className="h-4 w-4 mr-2" />
            Yeni Yazı Ekle
          </Button>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        <Button
          variant={selectedCategory === 'all' ? 'default' : 'outline'}
          onClick={() => setSelectedCategory('all')}
          className={selectedCategory === 'all' ? 'bg-[#8B7355]' : ''}
        >
          Tümü ({texts.length})
        </Button>
        {categories.map(cat => {
          const count = texts.filter(t => t.category === cat.value).length;
          return (
            <Button
              key={cat.value}
              variant={selectedCategory === cat.value ? 'default' : 'outline'}
              onClick={() => setSelectedCategory(cat.value)}
              className={selectedCategory === cat.value ? 'bg-[#8B7355]' : ''}
            >
              {cat.label} ({count})
            </Button>
          );
        })}
      </div>

      {/* Texts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredTexts.map(text => (
          <Card key={text.id} className="p-4 border-[#E8DCC8]">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="text-xs text-[#8B7355] mb-1">{text.key}</div>
                <div className="font-semibold text-[#5C4033] mb-1">{text.value}</div>
                <div className="text-xs text-[#B8A894]">{text.description}</div>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleOpenDialog(text)}
                  className="h-8 w-8 p-0"
                >
                  <Edit2 className="h-4 w-4 text-[#8B7355]" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(text.id)}
                  className="h-8 w-8 p-0"
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
            <span className="inline-block px-2 py-1 text-xs bg-[#F5EFE7] text-[#8B7355] rounded">
              {categories.find(c => c.value === text.category)?.label}
            </span>
          </Card>
        ))}
      </div>

      {filteredTexts.length === 0 && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-[#D5C4A1] mx-auto mb-4" />
          <p className="text-[#8B7355]">Henüz yazı eklenmemiş. Varsayılanları yükleyebilirsiniz.</p>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl bg-[#F9F6F1]">
          <DialogHeader>
            <DialogTitle className="text-[#5C4033]">
              {editingText ? 'Yazı Düzenle' : 'Yeni Yazı Ekle'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">
                Anahtar (Key) <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                placeholder="homepage.title"
                required
                className="bg-white border-[#D5C4A1]"
              />
              <p className="text-xs text-[#8B7355]">Örnek: homepage.title, nav.home, button.listen</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">
                Değer (Value) <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder="Kuran-ı Kerim"
                required
                rows={3}
                className="bg-white border-[#D5C4A1]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">
                Açıklama <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Anasayfa başlık"
                required
                className="bg-white border-[#D5C4A1]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">
                Kategori <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full p-2 border border-[#D5C4A1] rounded-md bg-white"
                required
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="border-[#D5C4A1]"
              >
                İptal
              </Button>
              <Button
                type="submit"
                className="bg-[#8B7355] hover:bg-[#6B5744]"
              >
                {editingText ? 'Güncelle' : 'Ekle'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TextManagement;
